/**
 * @fileoverview Target-period computation (Apr–Mar financial year).
 * @description Every spec's "Period Rules" table defines the same 4 period types against the same
 * Apr–Mar financial year — "Custom target date ranges are not supported." This is the one place
 * that turns a period type plus a specific period value into concrete start/end dates and a
 * display label, and the one place that generates the selectable period values for a picker, so
 * every page that assigns a target computes dates identically rather than each re-deriving the FY
 * math.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import type { TargetPeriodType } from '@/features/sales/types/tenant.types'

export interface PeriodOption {
  /** The value a period picker's second select stores — format depends on periodType (see
   * computePeriod's doc comment). */
  value: string
  label: string
}

export interface ComputedPeriod {
  startDate: string
  endDate: string
  periodLabel: string
}

const MONTH_NAMES = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
]

/**
 * @description Pads a number to 2 digits.
 * @param {number} value - The number to pad.
 * @returns {string} The 2-digit, zero-padded string.
 */
function pad2(value: number): string {
  return String(value).padStart(2, '0')
}

/**
 * @description Formats a date as an ISO yyyy-mm-dd string.
 * @param {number} year - The calendar year.
 * @param {number} month1 - The calendar month, 1-indexed (1 = January).
 * @param {number} day - The day of month.
 * @returns {string} The yyyy-mm-dd string.
 */
function isoDate(year: number, month1: number, day: number): string {
  return `${year}-${pad2(month1)}-${pad2(day)}`
}

/**
 * @description The last calendar day of a given month.
 * @param {number} year - The calendar year.
 * @param {number} month1 - The calendar month, 1-indexed (1 = January).
 * @returns {number} The number of days in that month.
 */
function lastDayOfMonth(year: number, month1: number): number {
  return new Date(year, month1, 0).getDate()
}

/**
 * @description Resolves the financial year's start calendar year (the "26" in "FY26-27") for the
 * current date — the FY runs April through March, so Jan-Mar belongs to the FY that started the
 * previous April.
 * @param {Date} [now] - The reference date (defaults to the current time; overridable for tests).
 * @returns {number} The FY's starting calendar year.
 */
export function currentFinancialYearStartYear(now = new Date()): number {
  return now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1
}

/**
 * @description Formats a financial year's start year as "FY26-27"-style label.
 * @param {number} fyStartYear - The FY's starting calendar year.
 * @returns {string} The "FY{yy}-{yy+1}" label.
 */
export function financialYearLabel(fyStartYear: number): string {
  return `FY${String(fyStartYear).slice(-2)}-${String(fyStartYear + 1).slice(-2)}`
}

/**
 * @description Computes a target's start date, end date, and display label from a period type and
 * a specific period value. Value formats: Monthly = "yyyy-mm"; Quarterly = "{fyStartYear}-Q1".."Q4"
 * (Q1 Apr-Jun, Q2 Jul-Sep, Q3 Oct-Dec, Q4 Jan-Mar of fyStartYear+1); Half-Yearly =
 * "{fyStartYear}-H1"|"H2" (H1 Apr-Sep, H2 Oct-Mar of fyStartYear+1); Annual = "{fyStartYear}" (Apr 1
 * fyStartYear to Mar 31 fyStartYear+1).
 * @param {TargetPeriodType} periodType - The period granularity.
 * @param {string} value - The specific period, in the format documented above for that type.
 * @returns {ComputedPeriod} The derived start date, end date, and display label.
 */
export function computePeriod(periodType: TargetPeriodType, value: string): ComputedPeriod {
  if (periodType === 'Monthly') {
    const [yearStr, monthStr] = value.split('-')
    const year = Number(yearStr)
    const month1 = Number(monthStr)
    return {
      startDate: isoDate(year, month1, 1),
      endDate: isoDate(year, month1, lastDayOfMonth(year, month1)),
      periodLabel: `${MONTH_NAMES[month1 - 1]} ${year}`,
    }
  }

  if (periodType === 'Quarterly') {
    const [fyStr, quarter] = value.split('-')
    const fyStartYear = Number(fyStr)
    const QUARTERS: Record<
      string,
      { startMonth1: number; startYear: number; endMonth1: number; endYear: number }
    > = {
      Q1: { startMonth1: 4, startYear: fyStartYear, endMonth1: 6, endYear: fyStartYear },
      Q2: { startMonth1: 7, startYear: fyStartYear, endMonth1: 9, endYear: fyStartYear },
      Q3: { startMonth1: 10, startYear: fyStartYear, endMonth1: 12, endYear: fyStartYear },
      Q4: { startMonth1: 1, startYear: fyStartYear + 1, endMonth1: 3, endYear: fyStartYear + 1 },
    }
    const range = QUARTERS[quarter ?? 'Q1']!
    return {
      startDate: isoDate(range.startYear, range.startMonth1, 1),
      endDate: isoDate(
        range.endYear,
        range.endMonth1,
        lastDayOfMonth(range.endYear, range.endMonth1),
      ),
      periodLabel: `${quarter} ${financialYearLabel(fyStartYear)} (${MONTH_NAMES[range.startMonth1 - 1]}-${MONTH_NAMES[range.endMonth1 - 1]} ${range.startYear})`,
    }
  }

  if (periodType === 'Half-Yearly') {
    const [fyStr, half] = value.split('-')
    const fyStartYear = Number(fyStr)
    if (half === 'H1') {
      return {
        startDate: isoDate(fyStartYear, 4, 1),
        endDate: isoDate(fyStartYear, 9, lastDayOfMonth(fyStartYear, 9)),
        periodLabel: `H1 ${financialYearLabel(fyStartYear)} (Apr-Sep ${fyStartYear})`,
      }
    }
    return {
      startDate: isoDate(fyStartYear, 10, 1),
      endDate: isoDate(fyStartYear + 1, 3, lastDayOfMonth(fyStartYear + 1, 3)),
      periodLabel: `H2 ${financialYearLabel(fyStartYear)} (Oct ${fyStartYear}-Mar ${fyStartYear + 1})`,
    }
  }

  // Annual
  const fyStartYear = Number(value)
  return {
    startDate: isoDate(fyStartYear, 4, 1),
    endDate: isoDate(fyStartYear + 1, 3, lastDayOfMonth(fyStartYear + 1, 3)),
    periodLabel: financialYearLabel(fyStartYear),
  }
}

/**
 * @description Builds the selectable period-value options for a given period type, spanning the
 * current financial year (Monthly: that FY's 12 months; Quarterly/Half-Yearly/Annual: the current
 * FY plus the next one, so a target can be planned slightly ahead).
 * @param {TargetPeriodType} periodType - The period granularity to build options for.
 * @param {Date} [now] - The reference date (defaults to the current time; overridable for tests).
 * @returns {PeriodOption[]} The `{ value, label }` options, in chronological order.
 */
export function periodOptionsFor(periodType: TargetPeriodType, now = new Date()): PeriodOption[] {
  const fyStartYear = currentFinancialYearStartYear(now)

  if (periodType === 'Monthly') {
    const months: PeriodOption[] = []
    for (let offset = 0; offset < 12; offset += 1) {
      const year = offset < 9 ? fyStartYear : fyStartYear + 1
      const month1 = ((offset + 3) % 12) + 1
      const value = `${year}-${pad2(month1)}`
      months.push({ value, label: computePeriod('Monthly', value).periodLabel })
    }
    return months
  }

  if (periodType === 'Quarterly') {
    return [fyStartYear, fyStartYear + 1].flatMap((fy) =>
      ['Q1', 'Q2', 'Q3', 'Q4'].map((quarter) => {
        const value = `${fy}-${quarter}`
        return { value, label: computePeriod('Quarterly', value).periodLabel }
      }),
    )
  }

  if (periodType === 'Half-Yearly') {
    return [fyStartYear, fyStartYear + 1].flatMap((fy) =>
      ['H1', 'H2'].map((half) => {
        const value = `${fy}-${half}`
        return { value, label: computePeriod('Half-Yearly', value).periodLabel }
      }),
    )
  }

  // Annual
  return [fyStartYear, fyStartYear + 1].map((fy) => ({
    value: String(fy),
    label: financialYearLabel(fy),
  }))
}

/**
 * @description Tests whether two date ranges (each inclusive, "yyyy-mm-dd" or full ISO) overlap at
 * all — used to decide whether a target (its own fixed startDate/endDate) falls within a selected
 * reporting-period filter, since a target's period and the filter's selected period are frequently
 * different granularities (e.g. a Quarterly target against an Annual filter).
 * @param {string} aStart - The first range's start date.
 * @param {string} aEnd - The first range's end date.
 * @param {string} bStart - The second range's start date.
 * @param {string} bEnd - The second range's end date.
 * @returns {boolean} Whether the two ranges share at least one day.
 */
export function periodsOverlap(
  aStart: string,
  aEnd: string,
  bStart: string,
  bEnd: string,
): boolean {
  return aStart <= bEnd && aEnd >= bStart
}

/**
 * @description Resolves the period value (in `computePeriod`'s own format for that period type)
 * containing the current date — a filter's sensible default, so e.g. a Quarterly filter opens on
 * "this quarter" rather than an arbitrary first option.
 * @param {TargetPeriodType} periodType - The period granularity to resolve a current value for.
 * @param {Date} [now] - The reference date (defaults to the current time; overridable for tests).
 * @returns {string} The current period's value, in the format `computePeriod` expects.
 */
export function currentPeriodValueFor(periodType: TargetPeriodType, now = new Date()): string {
  const fyStartYear = currentFinancialYearStartYear(now)
  const month1 = now.getMonth() + 1

  if (periodType === 'Monthly') {
    const year = month1 >= 4 ? fyStartYear : fyStartYear + 1
    return `${year}-${pad2(month1)}`
  }

  if (periodType === 'Quarterly') {
    if (month1 >= 4 && month1 <= 6) return `${fyStartYear}-Q1`
    if (month1 >= 7 && month1 <= 9) return `${fyStartYear}-Q2`
    if (month1 >= 10 && month1 <= 12) return `${fyStartYear}-Q3`
    return `${fyStartYear}-Q4`
  }

  if (periodType === 'Half-Yearly') {
    return month1 >= 4 && month1 <= 9 ? `${fyStartYear}-H1` : `${fyStartYear}-H2`
  }

  // Annual
  return String(fyStartYear)
}

/**
 * @description Resolves the period value (in `computePeriod`'s own format) that contains a given
 * ISO date — the inverse of `computePeriod`'s start date, used to pre-fill an edit form from a
 * stored target's `startDate`. Parsed as a local calendar date, not via `new Date(iso)`, which
 * would read it as UTC midnight and can land on the previous day in timezones behind UTC.
 * @param {TargetPeriodType} periodType - The period granularity to resolve a value for.
 * @param {string} isoDate - A "yyyy-mm-dd" date inside the wanted period.
 * @returns {string} The containing period's value, in the format `computePeriod` expects.
 */
export function periodValueForDate(periodType: TargetPeriodType, isoDate: string): string {
  const [year, month1, day] = isoDate.split('-').map(Number)
  return currentPeriodValueFor(periodType, new Date(year ?? 0, (month1 ?? 1) - 1, day ?? 1))
}
